import { differenceInCalendarDays } from "date-fns";
import BaseClient from "./BaseClient";

export default class RegularPaymentServiceClient extends BaseClient {
    constructor(billingDate = null, tenantId: string) {
        super(billingDate, tenantId, true, "rp");
    }

    public async getHistory(accountId) {
        const response = await this.get(`/api/proxy/rp/regular-payments-service/accounts/${accountId}/history`);
        return await this.handleResponse<any>(response);
    }

    public async getPaymentHistory(accountId) {
        const response = await this.get(`/api/proxy/rp/regular-payments-service/accounts/${accountId}/payment-history`);
        return await this.handleResponse<any>(response);
    }

    public async getRegularPayments(accountId) {
        const response = await this.get(`/api/proxy/rp/regular-payments-service/accounts/${accountId}`);
        return await this.handleResponse<any>(response, true);
    }

    public async cancelRegularPayment(accountId) {
        const response = await this.post(`/api/proxy/rp/regular-payments-service/accounts/${accountId}/cancel`, {});
        return await this.handleResponse<any>(response);
    }

    public async getProjectedPayments(accountId) {
        const response = await this.get(`/api/proxy/rp/regular-payments-service/accounts/${accountId}/projected-payment`);
        return await this.handleResponse<any>(response, true);
    }

    public async createRegularPayment(accountId, bankAccountNumber, sortCode, paymentType, fixedAmount, percentageAmount, collectionDateAdjustmentDays, delayFirstCollectionToNextPeriod) {
        const response = await this.post(`/api/proxy/rp/regular-payments-service/accounts/${accountId}`, {
            bankAccountNumber,
            sortCode,
            paymentType,
            fixedAmount: (paymentType === 'FixedAmount' || paymentType === 'FixedDDPlus') ? fixedAmount : undefined,
            percentageAmount: (paymentType == 'PercentageAmount') ? percentageAmount : undefined,
            collectionDateAdjustmentDays: collectionDateAdjustmentDays,
            delayFirstCollectionToNextPeriod: delayFirstCollectionToNextPeriod
        });
        return await this.handleResponse(response);
    }

    public async updateRegularPayment(accountId, bankAccountNumber, sortCode, paymentType, fixedAmount, percentageAmount, collectionDateAdjustmentDays) {
        const response = await this.put(`/api/proxy/rp/regular-payments-service/accounts/${accountId}`, {
            bankAccountNumber,
            sortCode,
            paymentType,
            fixedAmount: (paymentType === 'FixedAmount' || paymentType === 'FixedDDPlus') ? fixedAmount : undefined,
            percentageAmount: (paymentType == 'PercentageAmount') ? percentageAmount : undefined,
            collectionDateAdjustmentDays
        });
        return await this.handleResponse(response);
    }

    public async getOpenJobs(accountId) {
        const response = await this.get(`/api/proxy/rp/scheme-integration-service/jobs/account/${accountId}?openOnly=true`)

        return await this.handleResponse(response, true);
    }

    public async runJobs(accountId, startDate) {
        const contextDateTimeOffset = `${differenceInCalendarDays(startDate, new Date())}:0:00:00`;
        const response = await this.post(
            `/api/proxy/rp/scheme-integration-service/debug/account/${accountId}/run`,
            {
                "RunAsToday": true
            },
            {
                'context-datetimeoffset': contextDateTimeOffset
            })

        return await this.handleResponse(response, true);
    }

    public async updateCollectionDate(accountId, collectionDateAdjustmentDays){
        const response = await this.put(`/api/proxy/rp/regular-payments-service/accounts/${accountId}/collection-date`, {
            days: collectionDateAdjustmentDays,
        });
        return await this.handleResponse(response);
    }

    public async checkReports(reportType: string): Promise<CheckReportsResponse> {
        const response = await this.post(`/api/proxy/rp/scheme-integration-service/debug/check-reports?reportType=${reportType}`, {});
        return await this.handleResponse<CheckReportsResponse>(response, true);
    }
}

export const useRegularPaymentService = (billingDate = null, tenantId: string): RegularPaymentServiceClient => {
    return new RegularPaymentServiceClient(billingDate, tenantId);
};
       
export type CheckReportsResponse =
{
    ReportsChecked: CheckReportResponse[]
}

type CheckReportResponse =
{
        Id: string,
        ReportItems: ReportItem[]

}

type ReportItem =
{
    AccountId:string
    SortCode:string
    BankAccountNumber:string 
    ResponseCode:string
    ReportItemType: ReportItemType
}

enum ReportItemType {
    Unknown,
    SetupRejection,
    SetupAlert,
    PaymentRejection,
    Cancellation,
    Amendment
}